import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { AuthResponse, LoginResponse, PreAuthResponse } from '@tsin-core/store/auth/auth.model';
import { environment } from '@tsin-env/environment';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly baseUrl = `${environment.authBaseUrl}/auth`;
  private readonly WWUBaseUrl = `${environment.authBaseUrl}/auth`;
  private readonly WWUBaseUrl2 = `${environment.WWUBaseUrl}/auth`;

  private http = inject(HttpClient);

  signup(payload: { email: string; password: string; inviteCode: string }): Observable<any> {
    return this.http.post(`${this.baseUrl}/signup`, payload);
  }

  login(payload: { email: string; password: string }): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(`${this.baseUrl}/login`, payload);
  }

  refreshToken(payload: { refreshToken: string }): Observable<{ accessToken: string, expireIn: number }> {
    return this.http.post<{ accessToken: string, expireIn: number }>(`${this.baseUrl}/refresh`, payload);
  }

  continueWithEmail(payload: { email: string }): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(`${this.baseUrl}/passkey/authentication/options`, payload);
  }

  validateWithEmail(payload: { email: string }): Observable<PreAuthResponse> {
    return this.http.get<PreAuthResponse>(`${this.baseUrl}/email/${payload.email}`);
  }

  passkeyAuthenticationOptions(payload: { email: string }): Observable<any> {
    return this.http.post(`${this.baseUrl}/passkey/authentication/options`, payload);
  }

  passkeyRegistrationOptions(payload: { authId: string; deviceName: string }): Observable<any> {
    return this.http.post(`${this.baseUrl}/passkey/registration/options`, payload);
  }

  passkeyRegistrationVerify(payload: {
    deviceName: string;
    response: any;
    authId: string;
  }): Observable<any> {
    return this.http.post(`${this.baseUrl}/passkey/registration/verify`, payload);
  }

  passkeyLogin(payload: { email: string; response: any; rememberMe: boolean; deviceId: string }): Observable<LoginResponse> {
    return this.http.post<LoginResponse>(`${this.baseUrl}/passkey/login`, payload);
  }

  loginWWU(payload: { email: string; password: string }): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(`${this.WWUBaseUrl}/login`, payload);
  }

  changePassword(payload: { authId: string; currentPassword: string; newPassword: string }): Observable<any> {
    return this.http.post(`${this.baseUrl}/change-password`, payload);
  }

  sendForgotPasswordRequest(payload: { email: string, redirectUrl: string }): Observable<any> {
    return this.http.post(`${this.baseUrl}/send-forgot-password-request`, payload);
  }

  verifyForgotPasswordRequest(payload: { authId: string; resetToken: string; password: string }): Observable<any> {
    return this.http.post(`${this.baseUrl}/verify-forgot-password-request`, payload);
  }

  resendVerificationEmail(payload: { email: string }): Observable<any> {
    return this.http.patch(`${this.WWUBaseUrl}/resend-verification-email/${payload.email}`, { email: payload.email });
  }

  generateOtp(payload: { email: string; authId: string }): Observable<any> {
    return this.http.post(`${this.baseUrl}/generate-otp`, payload);
  }

  generateOtpWWU(payload: { email: string; authId: string }): Observable<any> {
    return this.http.post(`${this.WWUBaseUrl}/generate-otp`, payload);
  }

  verifyOtp(payload: { email: string; token: string }): Observable<any> {
    return this.http.post(`${this.baseUrl}/verify-otp`, payload);
  }

  verifyOtpWWU(payload: { email: string; token: string }): Observable<any> {
    return this.http.post(`${this.WWUBaseUrl}/verify-otp`, payload);
  }

  verifyEmail(payload: { email: string; password: string, code: string }): Observable<any> {
    return this.http.post(`${this.baseUrl}/verify-email`, payload);
  }

  verifyEmailWWU(payload: { email: string; password: string, code: string }): Observable<any> {
    return this.http.post(`${this.WWUBaseUrl2}/verify-email`, payload);
  }

  validateOtp(payload: { email: string; token: string }): Observable<any> {
    return this.http.post(`${this.baseUrl}/validate-otp`, payload);
  }

  ValidateOtpWWU(payload: { email: string; token: string }): Observable<any> {
    return this.http.post(`${this.WWUBaseUrl}/validate-otp`, payload);
  }

  disableOtp(payload: { email: string; authId: string }): Observable<any> {
    return this.http.post(`${this.baseUrl}/disable-otp`, payload);
  }

  getMe(): Observable<any> {
    return this.http.get(`${this.baseUrl}/me`);
  }

  createAccount(payload: {
    firstName: string;
    lastName: string;
    email: string;
    office: string;
    role: string;
    gender: string;
    dateOfBirth: string;
    password: string
  }): Observable<any> {
    return this.http.post(`${this.baseUrl}/create-account`, payload);
  }

  enableEmail2FA(id: string): Observable<any> {
    return this.http.get(`${this.baseUrl}/enable-email-2fa/${id}`);
  }

  enableEmail2FAWWU(id: string): Observable<any> {
    return this.http.get(`${this.WWUBaseUrl}/enable-email-2fa/${id}`);
  }

  confirmEmail2FA(otp: string): Observable<any> {
    return this.http.get(`${this.baseUrl}/confirm-email-2fa/${otp}`);
  }

  confirmEmail2FAWWU(otp: string): Observable<any> {
    return this.http.get(`${this.WWUBaseUrl}/confirm-email-2fa/${otp}`);
  }

  signUpWorkWithUs(payload: {
    firstName: string;
    lastName: string;
    email: string;
    office: string;
    role: string;
    gender: string;
    dateOfBirth: string;
    password: string
  }) {
    return this.http.post(`${this.WWUBaseUrl}/signup`, payload);
  }

  // Passkey Management Methods
  startPasskeyRegistration(passkeyName: string): Observable<any> {
    return this.http.post(`${this.baseUrl}/passkey/registration/start`, { passkeyName });
  }

  completePasskeyRegistration(payload: {
    passkeyName: string;
    credential: any;
    registrationOptions: any;
  }): Observable<any> {
    return this.http.post(`${this.baseUrl}/passkey/registration/complete`, payload);
  }

  getUserPasskeys(): Observable<any[]> {
    return this.http.get<any[]>(`${this.baseUrl}/passkey/list`);
  }

  deletePasskey(passkeyId: string): Observable<any> {
    return this.http.delete(`${this.baseUrl}/passkey/${passkeyId}`);
  }
}
